import { Timestamp } from '@bufbuild/protobuf';
import { CustomUploadResponse } from '@geneo2-web/shared-ui';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  AssessmentStudentResponse,
  StudentAssessAttemptResult,
  StudentAssessCountInfo,
  StudentAssessmentTask,
} from '@protos/learning_management/lms.assessment.student.apis_pb';
import {
  IdleTimeInformation,
  TimeStampWithKey,
} from '@protos/learning_management/lms.db_pb';
import { HomeworkContent } from '@protos/learning_management/lms.hw.common.apis_pb';
import {
  HWStudentResponse,
  StudentHWAttemptResult,
  StudentHWCountInfo,
  StudentHomeworkTask,
} from '@protos/learning_management/lms.hw.student.apis_pb';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
// import {} from '@protos/common/'

interface ProctoringViolationsSummary {
  fullScreenExits?: Timestamp[];
  tabSwitches?: Timestamp[];
  copyAttempts?: TimeStampWithKey[];
  pasteAttempts?: TimeStampWithKey[];
  rightClickAttempts?: Timestamp[];
  blockedShortcuts?: TimeStampWithKey[];
  idletimeData?: IdleTimeInformation[];
  currentLocation?: GeolocationCoordinates; // hold (will be handled in Backend)
  multipleSessionsData?: Timestamp[]; // needs discussion
}

export interface IHomeworkState {
  selected_module_id: string;
  home_homework_list?: StudentAssessmentTask[];
  ended_homework_list?: StudentAssessmentTask[];
  active_homework_Id?: string;
  active_homework_content?: {
    [homeworkId: string]: AssessmentContent | undefined;
  };
  active_homework_student_response?: {
    [homeworkId: string]: AssessmentStudentResponse | undefined;
  };
  active_homework_attempt_result?: {
    [homeworkId: string]: StudentAssessAttemptResult | undefined;
  };
  homework_end_path?: string;
  active_homework_session_id?: number;
  selected_homework_data?: {
    homeworkId: number;
    questionIds: string[];
    resumeCheck: boolean;
    reattemptedCheck: boolean;
    SubmissionType: number;
    elapsedTime?: number;
  };
  assigned_homework?: { [pageNumber: number]: StudentAssessmentTask[] };
  ended_homework?: { [pageNumber: number]: StudentAssessmentTask[] };
  hw_info?: {
    hwListInfo: StudentAssessCountInfo | undefined;
    totalAssignedPages: number;
    totalEndedPages: number;
  };
  student_hw_upload_data: CustomUploadResponse[];
  all_answers: string[];
  proctoring_violations_summary?: ProctoringViolationsSummary;
}

const initialState: IHomeworkState = {
  selected_module_id: 'abcd',
  home_homework_list: [],
  ended_homework_list: [],
  active_homework_content: undefined,
  active_homework_student_response: undefined,
  active_homework_attempt_result: undefined,
  active_homework_Id: undefined,
  homework_end_path: undefined,
  selected_homework_data: undefined,
  assigned_homework: {},
  ended_homework: {},
  hw_info: {
    hwListInfo: undefined,
    totalAssignedPages: 1,
    totalEndedPages: 1,
  },
  student_hw_upload_data: [],
  all_answers: [],
  proctoring_violations_summary: undefined,
};

export const homeworkSlice = createSlice({
  name: 'homework',
  initialState,
  reducers: {
    setActiveHomeworkList: (state, action) => {
      state.home_homework_list = action.payload;
    },
    setEndedHomeworkList: (state, action) => {
      state.ended_homework_list = action.payload;
    },
    setActiveHomeworkId: (state, action) => {
      state.active_homework_Id = action.payload;
    },
    setActiveHomeworkContent: (
      state,
      action: PayloadAction<{
        homeworkId: number;
        homeworkContent?: AssessmentContent;
      }>
    ) => {
      state.active_homework_content = {
        ...state.active_homework_content,
        [action.payload.homeworkId.toString()]: action.payload.homeworkContent,
      };
    },
    setActiveHomeworkStudentResponse: (
      state,
      action: PayloadAction<{
        homeworkId: number;
        response?: AssessmentStudentResponse;
      }>
    ) => {
      state.active_homework_student_response = {
        ...state.active_homework_student_response,
        [action.payload.homeworkId.toString()]: action.payload.response,
      };
    },
    setActiveHomeworSessionId: (state, action) => {
      state.active_homework_session_id = action.payload;
    },
    setActiveHomeworkAttemptResult: (
      state,
      action: PayloadAction<{
        homeworkId: number;
        result?: StudentAssessAttemptResult;
      }>
    ) => {
      state.active_homework_attempt_result = {
        ...state.active_homework_attempt_result,
        [action.payload.homeworkId.toString()]: action.payload.result,
      };
    },
    setHomeworkEndPath: (state, action: PayloadAction<string | undefined>) => {
      state.homework_end_path = action.payload;
    },
    setSelectedHomeworkData: (state, action) => {
      state.selected_homework_data = action.payload;
    },
    setAssignedData: (state, action) => {
      state.assigned_homework = action.payload;
    },
    setEndedData: (state, action) => {
      state.ended_homework = action.payload;
    },
    setHwInfoData: (state, action) => {
      state.hw_info = action.payload;
    },

    setStudentHwUploadData: (state, action) => {
      state.student_hw_upload_data = action.payload;
    },
    setAllAnswers: (state, action) => {
      state.all_answers = action.payload;
    },

    setProctoringViolationsSummary: (
      state,
      action: PayloadAction<ProctoringViolationsSummary | undefined>
    ) => {
      state.proctoring_violations_summary = action.payload;
    },

    resetHomeworkState: (state) => {
      return initialState;
    },
  },
});

export const {
  setActiveHomeworkList,
  setEndedHomeworkList,
  setActiveHomeworkId,
  setActiveHomeworkContent,
  setActiveHomeworkStudentResponse,
  setActiveHomeworkAttemptResult,
  resetHomeworkState,
  setHomeworkEndPath,
  setActiveHomeworSessionId,
  setSelectedHomeworkData,
  setAssignedData,
  setEndedData,
  setHwInfoData,
  setStudentHwUploadData,
  setAllAnswers,
  setProctoringViolationsSummary,
} = homeworkSlice.actions;
export default homeworkSlice.reducer;
